import { createConnectionQuery } from '@/helpers/createSocketConnectionQuery';
import { socketEmitterAggregate } from '@/helpers/socketEmitterAggregate';

import { AbstractEmitter } from './AbstractEmitter';
import { logger } from './logger';
import { MicroQueue, timeoutScheduler } from './MicroQueue';
import type { TFiberType } from './SportsbookSocketManager';

export const EmitterEvents = {
  SEND_ERROR: 'send_error',
  SOCKET_AUTH_ERROR: 'socket_auth_error',
  SOCKET_CLOSED: 'socket_closed',
  SOCKET_CONNECTIVITY_ERROR: 'socket_connectivity_error',
  UNKNOWN_EVENT: 'unknown_event',
} as const;

export const SocketEvents = {
  BATCH: 'batch',
  CONFIG: 'config',
  ERROR: 'error',
  LEAVE: 'leave',
  RPC: 'rpc',
} as const;

export type TEmitterEvents = (typeof EmitterEvents)[keyof typeof EmitterEvents];

export const AllEmitterEvents = { ...SocketEvents, ...EmitterEvents } as const;

type TAllEmitterEvents = (typeof AllEmitterEvents)[keyof typeof AllEmitterEvents];

export const SocketSendEvents = {
  JOIN: 'join',
  LEAVE: 'leave',
  RPC: 'rpc',
} as const;

export type TSocketSendEvents = (typeof SocketSendEvents)[keyof typeof SocketSendEvents];

export interface SocketServiceOptions {
  language: string;
  token: string;
  cid: string;
  tokenExpiresAt: number;
  tokenReceivedAt: number;
  expired: boolean;
  socketHost: string;
}

type QueueItem = {
  action: 'join' | 'leave';
  model?: TFiberType;
  ids?: string[];
  payload?: any;
};

export class SocketEmitter extends AbstractEmitter<TAllEmitterEvents, any> {
  private static instance: SocketEmitter | null = null;

  private microSocketQueue: MicroQueue<QueueItem> | null = null;
  connectionPromise: Promise<void> | null = null;
  language: string = '';
  reconnectionCount: number = 0;

  ws: WebSocket | null = null;

  private constructor() {
    super();
  }

  static getInstance(): SocketEmitter {
    if (!SocketEmitter.instance) {
      SocketEmitter.instance = new SocketEmitter();
    }

    return SocketEmitter.instance;
  }

  static _destroy() {
    if (SocketEmitter.instance) {
      SocketEmitter.instance.cleanup();
    }
    SocketEmitter.instance = null;
  }

  connect({
    language,
    token,
    cid,
    tokenExpiresAt,
    tokenReceivedAt,
    expired,
    socketHost,
  }: SocketServiceOptions) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return Promise.resolve(this.ws);
    }
    const query = createConnectionQuery({
      cid,
      expired,
      language,
      token,
      tokenExpiresAt,
      tokenReceivedAt,
    });

    this.microSocketQueue = new MicroQueue(
      (items) => {
        for (const item of items) {
          this.ws?.send(JSON.stringify([item.action, item.model, item.ids || []]));
        }
      },
      {
        aggregate: socketEmitterAggregate,
      },
      timeoutScheduler(50),
    );

    return new Promise((resolve) => {
      this.ws = new WebSocket(`${socketHost}?${query}`);

      this.ws.onmessage = (event) => {
        const [model, payload] = JSON.parse(String(event.data));

        if (model === 'error') {
          this.emit(AllEmitterEvents.SOCKET_AUTH_ERROR, {
            data: null,
            model,
            payload,
          });
          return;
        }

        this.initEmitters();

        resolve(this.ws);
      };

      this.ws.onerror = async () => {
        if (this.ws) {
          this.ws.close(1000, 'manual');
        }

        this.emit(AllEmitterEvents.SOCKET_CONNECTIVITY_ERROR, {
          data: null,
          model: '',
          payload: null,
        });
      };
    });
  }

  getIsWSReady() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  initEmitters() {
    if (!this.ws) return;

    this.ws.onmessage = ({ data: message }) => {
      if (!message) return;

      const [model, payload, data] = JSON.parse(message);

      switch (model) {
        case 'config': {
          this.emit(AllEmitterEvents.CONFIG, { data, model, payload });
          return;
        }
        case 'leave': {
          this.emit(AllEmitterEvents.LEAVE, { data, model, payload });
          return;
        }
        case 'batch': {
          if (!payload.length) {
            return;
          }

          this.emit(AllEmitterEvents.BATCH, { data, model, payload });
          return;
        }
        case 'rpc': {
          this.emit(AllEmitterEvents.RPC, { data, model, payload });
          return;
        }

        case 'timestamp':
        // case 'config':
        case 'language':
        case 'version':
        case 'sessionId': {
          return;
        }

        case 'error': {
          this.emit(AllEmitterEvents.ERROR, { data, model, payload });
          return;
        }

        default:
          logger.error(`Received message with unknown model: ${model}`, { data, model, payload });
          this.emit(AllEmitterEvents.UNKNOWN_EVENT, { data, model, payload });
      }
    };

    this.ws.onclose = async ({ wasClean, code, reason }) => {
      const data = {
        data: null,
        model: '' as const,
        payload: {
          code,
          reason,
          wasClean,
        },
      };
      this.emit(AllEmitterEvents.SOCKET_CLOSED, data);
    };
  }

  async send(type: TSocketSendEvents, model: any, data: any) {
    try {
      if (!this.ws || !this.getIsWSReady()) {
        return;
      }

      if (type === 'rpc') {
        this.ws.send(JSON.stringify([type, model, data]));
        return;
      }

      this.microSocketQueue?.push({ action: type, ids: data, model });
    } catch (error: any) {
      logger.error('WebSocket send error:', error);
      this.emit(AllEmitterEvents.SEND_ERROR, { data, model, payload: error });
    }
  }

  closeConnection() {
    if (this.ws) {
      this.ws.close(1000, 'manual');
      this.ws = null;
    }

    this.cleanup();
  }

  cleanup() {
    if (this.ws) {
      try {
        this.ws.close(1000, 'cleanup');
      } catch {
        console.warn('WebSocket cleanup error');
      }
    }

    this.ws = null;
    this.language = '';
    this.reconnectionCount = 0;
  }
}
